import { supabase } from '../lib/supabase'
import {
  applyFilters,
  applyOrdering,
  applyRange,
  applyRoleConstraints,
  applySearch,
  applyOwnershipToPayload,
  callRpc,
  handleQuery,
  accessDeniedResponse,
  resolveRoleContext,
  ensureCanManageEvent
} from './utils'

const TABLE_NAME = 'leads'
const ACTIVITIES_TABLE = 'lead_activities'

const LEAD_DETAIL_SELECT = `
  *,
  events(title, price),
  agent_sharing_links(slug, identifier),
  users!leads_agent_id_fkey(full_name, email),
  marketing_form_responses(
    id,
    submitted_at,
    score,
    marketing_forms(id, title, type)
  )
`

const LEAD_DETAIL_SELECT_FALLBACK = LEAD_DETAIL_SELECT.replace(
  'users!leads_agent_id_fkey',
  'users!agent_id'
)

const applyLeadAgentScope = (query, { role, userId, eventIds }) => {
  if (role !== 'agent' || !userId) {
    return query
  }

  let scopedQuery = query
  if (Array.isArray(eventIds) && eventIds.length > 0) {
    scopedQuery = scopedQuery.in('event_id', eventIds)
  }

  return scopedQuery.or(`agent_id.eq.${userId},primary_agent_id.eq.${userId}`)
}

const attachMeetingCounts = async (leads) => {
  if (!Array.isArray(leads) || leads.length === 0) {
    return { data: leads, error: null }
  }

  const leadIds = leads.map((lead) => lead.id).filter(Boolean)
  if (leadIds.length === 0) {
    return { data: leads, error: null }
  }

  const { data: activityRows, error } = await handleQuery(
    supabase
      .from('lead_activities')
      .select('lead_id')
      .in('lead_id', leadIds)
      .eq('activity_type', 'meeting'),
    { fallbackMessage: 'Unable to load meeting activity counts.' }
  )

  if (error) {
    return { data: null, error }
  }

  const countsByLead = activityRows.reduce((counts, row) => {
    if (!row?.lead_id) {
      return counts
    }
    counts[row.lead_id] = (counts[row.lead_id] || 0) + 1
    return counts
  }, {})

  return {
    data: leads.map((lead) => ({
      ...lead,
      meeting_count: countsByLead[lead.id] || 0
    })),
    error: null
  }
}

/**
 * Fetch leads with optional filtering and role constraints.
 * By default only non-archived (active) leads are returned; use includeArchived or archivedOnly to include or show only archived.
 * @param {{
 *   role?: 'student' | 'agent' | 'partner' | 'admin',
 *   userId?: string,
 *   eventIds?: string[],
 *   includeArchived?: boolean,
 *   archivedOnly?: boolean,
 *   filters?: Array<{ column: string, operator: string, value: any }>,
 *   search?: string,
 *   searchColumns?: string[],
 *   orderBy?: { column: string, ascending?: boolean },
 *   range?: { from: number, to: number }
 * }} [options]
 */
export const listLeads = async (options = {}) => {
  const {
    role,
    userId,
    eventIds,
    includeArchived = false,
    archivedOnly = false,
    filters,
    search,
    searchColumns = ['full_name', 'email', 'company'],
    orderBy = { column: 'created_at', ascending: false },
    range
  } = options

  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role
  const resolvedUserId = roleContext.userId

  if (resolvedRole === 'partner' && (!eventIds || eventIds.length === 0)) {
    return accessDeniedResponse('Partners can only access leads for assigned events.')
  }

  let query = supabase
    .from(TABLE_NAME)
    .select(`
      *,
      events(title, price),
      agent_sharing_links(slug, identifier),
      full_name,
      email,
      phone,
      invited_by
    `)

  if (resolvedRole === 'agent') {
    query = applyLeadAgentScope(query, {
      role: resolvedRole,
      userId: resolvedUserId,
      eventIds
    })
  } else {
    query = applyRoleConstraints(query, {
      role: resolvedRole,
      userId: resolvedUserId,
      agentField: 'agent_id',
      studentField: 'student_id',
      eventIds
    })
  }

  if (archivedOnly) {
    query = query.eq('archived', true)
  } else if (!includeArchived) {
    query = query.eq('archived', false)
  }

  query = applyFilters(query, filters)
  query = applySearch(query, search, searchColumns)
  query = applyOrdering(query, orderBy)
  query = applyRange(query, range)

  const { data, error } = await handleQuery(query, {
    fallbackMessage: 'Unable to load leads.'
  })

  if (error || !data) {
    return { data: null, error }
  }

  return attachMeetingCounts(data)
}

/**
 * Fetch a single lead by id.
 * @param {string} leadId
 * @param {{ role?: 'student' | 'agent' | 'partner' | 'admin', userId?: string, eventIds?: string[] }} [options]
 */
export const getLeadById = async (leadId, options = {}) => {
  const { role, userId, eventIds } = options

  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role
  const resolvedUserId = roleContext.userId

  if (resolvedRole === 'partner' && (!eventIds || eventIds.length === 0)) {
    return accessDeniedResponse('Partners can only access leads for assigned events.')
  }

  const runDetailQuery = (select) => {
    let query = supabase.from(TABLE_NAME).select(select).eq('id', leadId).maybeSingle()

    if (resolvedRole === 'agent') {
      query = applyLeadAgentScope(query, {
        role: resolvedRole,
        userId: resolvedUserId,
        eventIds
      })
    } else {
      query = applyRoleConstraints(query, {
        role: resolvedRole,
        userId: resolvedUserId,
        agentField: 'agent_id',
        studentField: 'student_id',
        eventIds
      })
    }

    return handleQuery(query, {
      fallbackMessage: 'Unable to load the lead.',
      allowEmpty: false
    })
  }

  let { data, error } = await runDetailQuery(LEAD_DETAIL_SELECT)

  if (error?.code === 'PGRST200') {
    ;({ data, error } = await runDetailQuery(LEAD_DETAIL_SELECT_FALLBACK))
  }

  if (error || !data) {
    return { data: null, error }
  }

  const meetingCounts = await attachMeetingCounts([data])
  if (meetingCounts.error) {
    return { data: null, error: meetingCounts.error }
  }

  return { data: meetingCounts.data?.[0] ?? data, error: null }
}

/**
 * Update lead pipeline status.
 * @param {string} leadId
 * @param {string} status
 * @param {{ role?: string, userId?: string, eventId?: string, eventIds?: string[] }} [options]
 */
export const updateLeadStatus = async (leadId, status, options = {}) =>
  updateLead(leadId, { status }, options)

/**
 * Update lead general notes.
 * @param {string} leadId
 * @param {string | null} notes
 * @param {{ role?: string, userId?: string, eventId?: string, eventIds?: string[] }} [options]
 */
export const updateLeadNotes = async (leadId, notes, options = {}) =>
  updateLead(leadId, { notes }, options)

/**
 * Meeting activities for a lead, newest first.
 * @param {string} leadId
 * @param {{ role?: string, userId?: string }} [options]
 */
export const getLeadActivities = async (leadId, options = {}) =>
  listLeadActivities(leadId, {
    ...options,
    activityType: 'meeting',
    orderBy: { column: 'created_at', ascending: false }
  })

/**
 * Users available as lead owners (agents/admins).
 * @param {{ role?: string, userId?: string }} [options]
 */
export const listLeadOwners = async (options = {}) => {
  const roleContext = await resolveRoleContext(options)
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  if (roleContext.role !== 'agent' && roleContext.role !== 'admin') {
    return accessDeniedResponse('Only agents and administrators can load lead owners.')
  }

  return handleQuery(
    supabase
      .from('users')
      .select('id, full_name, email, role')
      .order('full_name', { ascending: true }),
    { fallbackMessage: 'Unable to load lead owners.' }
  )
}

/**
 * Assign lead owner (agent and primary agent).
 * @param {string} leadId
 * @param {string} newAgentId
 * @param {{ role?: string, userId?: string, eventId?: string, eventIds?: string[] }} [options]
 */
export const updateLeadOwner = async (leadId, newAgentId, options = {}) =>
  updateLead(
    leadId,
    { agent_id: newAgentId, primary_agent_id: newAgentId },
    options
  )

/**
 * Set archived flag on a lead.
 * @param {string} leadId
 * @param {boolean} archived
 * @param {{ role?: string, userId?: string, eventId?: string, eventIds?: string[] }} [options]
 */
export const toggleLeadArchived = async (leadId, archived, options = {}) =>
  updateLead(leadId, { archived: Boolean(archived) }, options)

/**
 * Create a new lead.
 * @param {Record<string, any>} payload
 * @param {{ role?: 'student' | 'agent' | 'partner' | 'admin', userId?: string }} [options]
 */
export const createLead = async (payload, options = {}) => {
  const { role, userId } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role
  const resolvedUserId = roleContext.userId

  if (resolvedRole === 'partner' && !payload?.event_id) {
    return accessDeniedResponse('Partners can only create leads for assigned events.')
  }

  if ((resolvedRole === 'agent' || resolvedRole === 'partner') && payload?.event_id) {
    const access = await ensureCanManageEvent(payload.event_id)
    if (!access.allowed) {
      return { data: null, error: access.error }
    }
  }

  const data = applyOwnershipToPayload(payload, {
    role: resolvedRole,
    userId: resolvedUserId,
    agentField: 'agent_id',
    studentField: 'student_id'
  })

  const query = supabase.from(TABLE_NAME).insert(data).select('*').single()

  return handleQuery(query, {
    fallbackMessage: 'Unable to create the lead.',
    allowEmpty: false
  })
}

/**
 * Update a lead.
 * @param {string} leadId
 * @param {Record<string, any>} updates
 * @param {{ role?: 'student' | 'agent' | 'partner' | 'admin', userId?: string, eventId?: string, eventIds?: string[] }} [options]
 */
export const updateLead = async (leadId, updates, options = {}) => {
  const { role, userId, eventId, eventIds } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role
  const resolvedUserId = roleContext.userId

  if (resolvedRole === 'partner') {
    const scopedEventId = eventId || updates?.event_id
    if (!scopedEventId && (!eventIds || eventIds.length === 0)) {
      return accessDeniedResponse('Partners can only update leads for assigned events.')
    }
  }

  const scopedEventId = eventId || updates?.event_id
  if ((resolvedRole === 'agent' || resolvedRole === 'partner') && scopedEventId) {
    const access = await ensureCanManageEvent(scopedEventId)
    if (!access.allowed) {
      return { data: null, error: access.error }
    }
  }

  let query = supabase.from(TABLE_NAME).update(updates).eq('id', leadId)

  query = applyRoleConstraints(query, {
    role: resolvedRole,
    userId: resolvedUserId,
    agentField: 'agent_id',
    studentField: 'student_id',
    eventIds
  })

  query = query.select('*').single()

  return handleQuery(query, {
    fallbackMessage: 'Unable to update the lead.',
    allowEmpty: false
  })
}

/**
 * Archive a lead (soft delete). Agents and admins can archive.
 * @param {string} leadId
 * @param {{ role?: 'student' | 'agent' | 'partner' | 'admin', userId?: string, eventId?: string, eventIds?: string[] }} [options]
 */
export const archiveLead = async (leadId, options = {}) => {
  return updateLead(leadId, { archived: true }, options)
}

/**
 * Unarchive a lead. Agents and admins can unarchive.
 * @param {string} leadId
 * @param {{ role?: 'student' | 'agent' | 'partner' | 'admin', userId?: string, eventId?: string, eventIds?: string[] }} [options]
 */
export const unarchiveLead = async (leadId, options = {}) => {
  return updateLead(leadId, { archived: false }, options)
}

/**
 * Permanently delete a lead. Only admins can delete.
 * @param {string} leadId
 * @param {{ role?: 'student' | 'agent' | 'partner' | 'admin', userId?: string, eventId?: string, eventIds?: string[] }} [options]
 */
export const deleteLead = async (leadId, options = {}) => {
  const { role, userId, eventId, eventIds } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role
  const resolvedUserId = roleContext.userId

  if (resolvedRole !== 'admin') {
    return accessDeniedResponse('Only admins can permanently delete leads.')
  }

  let query = supabase.from(TABLE_NAME).delete().eq('id', leadId)

  query = applyRoleConstraints(query, {
    role: resolvedRole,
    userId: resolvedUserId,
    agentField: 'agent_id',
    studentField: 'student_id',
    eventIds
  })

  query = query.select('*').single()

  return handleQuery(query, {
    fallbackMessage: 'Unable to delete the lead.',
    allowEmpty: false
  })
}

/**
 * Update lead payment status (for backend/webhook use; service role or RPC preferred).
 * @param {string} leadId
 * @param {{ payment_status: string, payment_amount?: number, payment_date?: string, stripe_payment_intent_id?: string }} updates
 * @param {object} [options] - role/userId for RLS
 */
export const updateLeadPaymentStatus = async (leadId, updates, options = {}) => {
  const { payment_status, payment_amount, payment_date, stripe_payment_intent_id } = updates
  const payload = {}
  if (payment_status != null) payload.payment_status = payment_status
  if (payment_amount != null) payload.payment_amount = payment_amount
  if (payment_date != null) payload.payment_date = payment_date
  if (stripe_payment_intent_id != null) payload.stripe_payment_intent_id = stripe_payment_intent_id
  if (Object.keys(payload).length === 0) return { data: null, error: { message: 'No payment fields to update' } }
  return updateLead(leadId, payload, options)
}

/**
 * Mark lead as account created (link student_id and set account_created/account_created_at).
 * @param {string} leadId
 * @param {string} studentId - UUID of the created user/student
 * @param {object} [options] - role/userId for RLS
 */
export const markAccountCreated = async (leadId, studentId, options = {}) => {
  return updateLead(leadId, {
    student_id: studentId,
    account_created: true,
    account_created_at: new Date().toISOString()
  }, options)
}

/**
 * List activities for a lead (RLS enforced via lead ownership).
 * @param {string} leadId
 * @param {{
 *   activityType?: string,
 *   orderBy?: { column: string, ascending?: boolean },
 *   role?: 'student' | 'agent' | 'partner' | 'admin',
 *   userId?: string
 * }} [options]
 */
export const listLeadActivities = async (leadId, options = {}) => {
  if (!leadId) {
    return { data: [], error: { message: 'Lead id is required.' } }
  }

  const {
    activityType,
    orderBy = { column: 'activity_date', ascending: false },
    role,
    userId
  } = options

  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  let query = supabase
    .from(ACTIVITIES_TABLE)
    .select('id, lead_id, activity_type, activity_date, created_at, notes, duration_minutes, created_by')
    .eq('lead_id', leadId)

  if (activityType) {
    query = query.eq('activity_type', activityType)
  }

  query = applyOrdering(query, orderBy)

  return handleQuery(query, {
    fallbackMessage: 'Unable to load lead activities.'
  })
}

/**
 * Create a lead activity (meeting, call, note, etc.).
 * @param {{
 *   lead_id: string,
 *   activity_type: string,
 *   activity_date?: string,
 *   notes?: string,
 *   duration_minutes?: number | null,
 *   created_by?: string | null
 * }} payload
 * @param {{ role?: 'student' | 'agent' | 'partner' | 'admin', userId?: string }} [options]
 */
export const createLeadActivity = async (payload, options = {}) => {
  const { role, userId } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  if (!payload?.lead_id) {
    return { data: null, error: { message: 'lead_id is required.' } }
  }

  const data = {
    ...payload,
    created_by: payload.created_by ?? roleContext.userId ?? null
  }

  const query = supabase.from(ACTIVITIES_TABLE).insert(data).select('*').single()

  return handleQuery(query, {
    fallbackMessage: 'Unable to create lead activity.',
    allowEmpty: false
  })
}

/**
 * Fetch aggregated lead summary for an agent.
 * @param {{ agentId?: string, role?: 'student' | 'agent' | 'partner' | 'admin', userId?: string }} options
 */
export const getAgentLeadSummary = async (options = {}) => {
  const { agentId, role, userId } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role
  if (resolvedRole !== 'admin' && resolvedRole !== 'agent') {
    return accessDeniedResponse('Only agents can access lead summaries.')
  }

  const resolvedAgentId = agentId || (resolvedRole === 'agent' ? roleContext.userId : undefined)

  return callRpc('get_agent_lead_summary', {
    agent_id: resolvedAgentId
  }, 'Unable to load lead summary.')
}

/** Pipeline status values used in Lead Pipeline UI. */
export const PIPELINE_LEAD_STATUSES = ['new', 'contacted', '1_1_completed', 'converted', 'lost']

export const PIPELINE_STATUS_LABELS = {
  new: 'New',
  contacted: 'Contacted',
  '1_1_completed': '1-1 Completed',
  converted: 'Converted',
  lost: 'Lost'
}

const SESSION_FILTERS_KEY = 'leadPipelineFilters'

/**
 * Default filter state for the Lead Pipeline filter bar.
 * @returns {{ eventId: string, agentId: string, leadStatus: string, paymentStatus: string, dateFrom: string, dateTo: string, hasAccount: string }}
 */
export const getDefaultLeadPipelineFilters = () => ({
  eventId: '',
  agentId: '',
  leadStatus: '',
  paymentStatus: '',
  dateFrom: '',
  dateTo: '',
  hasAccount: ''
})

export const loadLeadPipelineFiltersFromSession = () => {
  if (typeof sessionStorage === 'undefined') {
    return getDefaultLeadPipelineFilters()
  }
  try {
    const raw = sessionStorage.getItem(SESSION_FILTERS_KEY)
    if (!raw) return getDefaultLeadPipelineFilters()
    const parsed = JSON.parse(raw)
    return { ...getDefaultLeadPipelineFilters(), ...parsed }
  } catch {
    return getDefaultLeadPipelineFilters()
  }
}

export const saveLeadPipelineFiltersToSession = (filters) => {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.setItem(SESSION_FILTERS_KEY, JSON.stringify(filters))
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * Distinct events and agent ids from a lead list (for filter dropdowns).
 * @param {Array<{ eventId?: string, eventTitle?: string, agentId?: string, primaryAgentId?: string }>} leads
 */
export const getDistinctLeadFilterOptions = (leads = []) => {
  const eventsById = new Map()
  const agentIds = new Set()

  for (const lead of leads) {
    const eventId = lead.eventId ?? lead.event_id
    if (eventId) {
      const title =
        lead.eventTitle ??
        lead.events?.title ??
        eventsById.get(eventId)?.title ??
        'Unknown Event'
      eventsById.set(eventId, { id: eventId, title })
    }
    const agentId = lead.agentId ?? lead.agent_id
    if (agentId) agentIds.add(agentId)
    const primaryAgentId = lead.primaryAgentId ?? lead.primary_agent_id
    if (primaryAgentId) agentIds.add(primaryAgentId)
  }

  const events = [...eventsById.values()].sort((a, b) =>
    (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' })
  )

  return { events, agentIds: [...agentIds] }
}

/**
 * Resolve payment filter category (paid / unpaid / free) for a lead.
 * Free matches display logic: event price is 0 and not paid.
 * @param {object} lead
 * @param {Array<{ id: string, price?: number }>} [eventsList]
 */
export const resolveLeadPaymentFilterCategory = (lead, eventsList = []) => {
  const paymentStatus = (lead.paymentStatus ?? lead.payment_status ?? 'pending').toLowerCase()
  if (paymentStatus === 'paid') return 'paid'

  const eventId = lead.eventId ?? lead.event_id
  const fromDashboard = eventsList.find((e) => e.id === eventId)
  let price = fromDashboard?.price != null ? Number(fromDashboard.price) : null
  if (price === null && lead.eventPrice != null) price = Number(lead.eventPrice)
  if (price !== null && price <= 0) return 'free'

  return 'unpaid'
}

/**
 * @param {object} filters
 */
export const countActiveLeadPipelineFilters = (filters = {}) => {
  const defaults = getDefaultLeadPipelineFilters()
  return Object.keys(defaults).filter((key) => {
    const value = filters[key]
    return value != null && String(value).trim() !== ''
  }).length
}

/**
 * Client-side pipeline filters (full lead list is loaded in dashboard context).
 * @param {Array} leads
 * @param {ReturnType<typeof getDefaultLeadPipelineFilters>} filters
 * @param {Array<{ id: string, price?: number }>} [eventsList]
 */
export const applyLeadPipelineFilters = (leads = [], filters = {}, eventsList = []) => {
  const f = { ...getDefaultLeadPipelineFilters(), ...filters }
  let list = [...leads]

  if (f.eventId) {
    list = list.filter((lead) => (lead.eventId ?? lead.event_id) === f.eventId)
  }

  if (f.agentId) {
    list = list.filter((lead) => {
      const agentId = lead.agentId ?? lead.agent_id
      const primaryAgentId = lead.primaryAgentId ?? lead.primary_agent_id
      return agentId === f.agentId || primaryAgentId === f.agentId
    })
  }

  if (f.leadStatus) {
    list = list.filter((lead) => (lead.status ?? 'new') === f.leadStatus)
  }

  if (f.paymentStatus) {
    list = list.filter(
      (lead) => resolveLeadPaymentFilterCategory(lead, eventsList) === f.paymentStatus
    )
  }

  if (f.dateFrom) {
    const from = new Date(f.dateFrom)
    from.setHours(0, 0, 0, 0)
    list = list.filter((lead) => {
      const created = lead.createdAt ?? lead.created_at ?? lead.registeredAt
      if (!created) return false
      return new Date(created) >= from
    })
  }

  if (f.dateTo) {
    const to = new Date(f.dateTo)
    to.setHours(23, 59, 59, 999)
    list = list.filter((lead) => {
      const created = lead.createdAt ?? lead.created_at ?? lead.registeredAt
      if (!created) return false
      return new Date(created) <= to
    })
  }

  if (f.hasAccount === 'yes') {
    list = list.filter((lead) => Boolean(lead.accountCreated ?? lead.account_created))
  } else if (f.hasAccount === 'no') {
    list = list.filter((lead) => !(lead.accountCreated ?? lead.account_created))
  }

  return list
}

/**
 * Fetch agent display labels for filter dropdown (admin only).
 * @param {string[]} agentIds
 * @param {{ role?: string, userId?: string }} [options]
 */
export const listAgentsForLeadFilter = async (agentIds = [], options = {}) => {
  const ids = [...new Set(agentIds.filter(Boolean))]
  if (ids.length === 0) {
    return { data: [], error: null }
  }

  const roleContext = await resolveRoleContext(options)
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }
  if (roleContext.role !== 'admin') {
    return accessDeniedResponse('Only admins can load agent filter options.')
  }

  const query = supabase
    .from('users')
    .select('id, full_name, email, role')
    .in('id', ids)
    .eq('role', 'agent')
    .order('full_name', { ascending: true })

  const { data, error } = await handleQuery(query, {
    fallbackMessage: 'Unable to load agents for filters.'
  })

  if (error) {
    return { data: null, error }
  }

  const agents = (data ?? []).map((row) => ({
    id: row.id,
    label: row.full_name?.trim() || row.email || row.id
  }))

  return { data: agents, error: null }
}

const EXPORT_BATCH_SIZE = 50

const FORM_RESPONSES_EXPORT_SELECT = `
  id,
  lead_id,
  form_id,
  submitted_at,
  score,
  passed,
  marketing_forms (
    id,
    title,
    type
  ),
  marketing_form_answers (
    answer_text,
    marketing_form_questions (
      question_text_es,
      question_text_en
    ),
    marketing_form_options (
      option_text_es,
      option_text_en
    )
  )
`

/**
 * Chunk an array into fixed-size batches.
 * @param {Array} items
 * @param {number} size
 */
const chunkArray = (items, size) => {
  const chunks = []
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size))
  }
  return chunks
}

/**
 * Group rows by lead_id into a plain object map.
 * @param {Array<{ lead_id?: string }>} rows
 */
const groupByLeadId = (rows) => {
  const grouped = {}
  for (const row of rows ?? []) {
    const leadId = row?.lead_id
    if (!leadId) continue
    if (!grouped[leadId]) grouped[leadId] = []
    grouped[leadId].push(row)
  }
  return grouped
}

/**
 * Batch-fetch lead activities for export (grouped by lead_id).
 * @param {string[]} leadIds
 * @param {{
 *   orderBy?: { column: string, ascending?: boolean },
 *   role?: string,
 *   userId?: string
 * }} [options]
 */
export const listLeadActivitiesByLeadIds = async (leadIds = [], options = {}) => {
  const ids = [...new Set(leadIds.filter(Boolean))]
  if (ids.length === 0) {
    return { data: {}, error: null }
  }

  const roleContext = await resolveRoleContext(options)
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const { orderBy = { column: 'activity_date', ascending: true } } = options
  const allRows = []

  for (const batch of chunkArray(ids, EXPORT_BATCH_SIZE)) {
    let query = supabase
      .from(ACTIVITIES_TABLE)
      .select('id, lead_id, activity_type, activity_date, created_at, notes, duration_minutes')
      .in('lead_id', batch)

    query = applyOrdering(query, orderBy)

    const { data, error } = await handleQuery(query, {
      fallbackMessage: 'Unable to load lead activities for export.'
    })

    if (error) {
      return { data: null, error }
    }

    if (Array.isArray(data)) {
      allRows.push(...data)
    }
  }

  return { data: groupByLeadId(allRows), error: null }
}

/**
 * Batch-fetch form responses with answers for export (grouped by lead_id).
 * @param {string[]} leadIds
 * @param {{ role?: string, userId?: string }} [options]
 */
export const listFormResponsesByLeadIds = async (leadIds = [], options = {}) => {
  const ids = [...new Set(leadIds.filter(Boolean))]
  if (ids.length === 0) {
    return { data: {}, error: null }
  }

  const roleContext = await resolveRoleContext(options)
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  if (roleContext.role !== 'admin' && roleContext.role !== 'agent') {
    return accessDeniedResponse('Only agents and administrators can export form responses.')
  }

  const allRows = []

  for (const batch of chunkArray(ids, EXPORT_BATCH_SIZE)) {
    const query = supabase
      .from('marketing_form_responses')
      .select(FORM_RESPONSES_EXPORT_SELECT)
      .in('lead_id', batch)
      .order('submitted_at', { ascending: true })

    const { data, error } = await handleQuery(query, {
      fallbackMessage: 'Unable to load form responses for export.'
    })

    if (error) {
      return { data: null, error }
    }

    if (Array.isArray(data)) {
      allRows.push(...data)
    }
  }

  return { data: groupByLeadId(allRows), error: null }
}

/**
 * Fetch agent name/email for lead export enrichment.
 * @param {string[]} agentIds
 * @param {{ role?: string, userId?: string }} [options]
 */
export const listAgentsForLeadsExport = async (agentIds = [], options = {}) => {
  const ids = [...new Set(agentIds.filter(Boolean))]
  if (ids.length === 0) {
    return { data: {}, error: null }
  }

  const roleContext = await resolveRoleContext(options)
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  if (roleContext.role !== 'admin' && roleContext.role !== 'agent') {
    return accessDeniedResponse('Only agents and administrators can load agent details for export.')
  }

  const query = supabase
    .from('users')
    .select('id, full_name, email')
    .in('id', ids)

  const { data, error } = await handleQuery(query, {
    fallbackMessage: 'Unable to load agents for export.'
  })

  if (error) {
    return { data: null, error }
  }

  const byId = {}
  for (const row of data ?? []) {
    byId[row.id] = {
      name: row.full_name?.trim() || row.email || row.id,
      email: row.email ?? ''
    }
  }

  return { data: byId, error: null }
}

const RPC_ATTRIBUTE_LEAD = 'attribute_lead'

/**
 * Create or update a lead via attribute_lead (set-returning RPC).
 * @param {{
 *   email: string,
 *   fullName?: string,
 *   full_name?: string,
 *   phone?: string | null,
 *   invitedBy?: string | null,
 *   invited_by?: string | null,
 *   sharingLinkCode?: string | null,
 *   sharing_link_code?: string | null,
 *   studentId?: string | null,
 *   student_id?: string | null,
 *   source?: string,
 *   eventId?: string | null,
 *   event_id?: string | null,
 *   agentId?: string | null,
 *   agent_id?: string | null,
 *   leadId?: string | null,
 *   lead_id?: string | null,
 *   status?: string | null
 * }} payload
 * @returns {Promise<{ data: string | null, error: object | null }>} lead UUID
 */
export const attributeLead = async (payload = {}) => {
  const email = (payload.email ?? '').trim()
  if (!email) {
    return {
      data: null,
      error: { message: 'Email is required to attribute a lead.', code: 'invalid_input' }
    }
  }

  const result = await callRpc(
    RPC_ATTRIBUTE_LEAD,
    {
      p_email: email,
      p_full_name: payload.fullName ?? payload.full_name ?? '',
      p_phone: payload.phone ?? null,
      p_invited_by: payload.invitedBy ?? payload.invited_by ?? null,
      p_sharing_link_code: payload.sharingLinkCode ?? payload.sharing_link_code ?? null,
      p_student_id: payload.studentId ?? payload.student_id ?? null,
      p_source: payload.source ?? 'agent_link',
      p_event_id: payload.eventId ?? payload.event_id ?? null,
      p_agent_id: payload.agentId ?? payload.agent_id ?? null,
      p_lead_id: payload.leadId ?? payload.lead_id ?? null,
      p_status: payload.status ?? null
    },
    'Unable to attribute lead.'
  )

  if (result.error) {
    return { data: null, error: result.error }
  }

  const rows = Array.isArray(result.data) ? result.data : result.data != null ? [result.data] : []
  const leadId = rows[0]?.lead_id ?? null

  if (!leadId) {
    return {
      data: null,
      error: { message: 'Lead attribution did not return a lead ID.', code: 'invalid_response' }
    }
  }

  return { data: leadId, error: null }
}
